/**
 * Queries and mutations for the Message Center.
 *
 * Its own key root rather than a branch of anything existing: the notice board
 * has no relationship to tickets, approvals or conversations, and nothing those
 * mutate makes it stale.
 *
 * The public list and the admin list are *different* resources, not the same
 * one filtered client-side — the admin list carries drafts and hidden rows that
 * the public endpoint must never return. Keeping them as separate keys means a
 * cache leak cannot turn an unpublished notice into a visible one.
 *
 * Every mutation invalidates the whole root. The lists are small and an admin
 * edit routinely moves a row between them (publish, or a placement change), so
 * surgical invalidation would buy nothing and get it wrong.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminCreateMessage as sdkAdminCreateMessage,
  adminDeleteMessage as sdkAdminDeleteMessage,
  adminListMessages as sdkAdminListMessages,
  adminMessageHistory as sdkAdminMessageHistory,
  adminUpdateMessage as sdkAdminUpdateMessage,
  getMessage as sdkGetMessage,
  listMessages as sdkListMessages,
  type Message,
  type MessageCreateRequest,
  type MessageEvent,
  type MessagePlacement,
  type MessageUpdateRequest,
} from "@/lib/api";
import { logger } from "@/lib/logger";

export const messageKeys = {
  all: ["messages"] as const,
  /** Published, visible notices. `placement` narrows to the home subset. */
  list: (placement: MessagePlacement | "ALL") => [...messageKeys.all, "list", placement] as const,
  /** Everything, drafts and hidden included. Admin-only server-side. */
  admin: () => [...messageKeys.all, "admin"] as const,
  history: (messageId: string) => [...messageKeys.all, "history", messageId] as const,
};

/**
 * How long a notice may be stale.
 *
 * A minute: an alert that IT has just published should reach an open tab
 * quickly, but the board is not a live feed and refetching it on every window
 * focus would be noise on a page people leave open all day.
 */
const MESSAGE_STALE_MS = 60_000;

async function listMessages(placement?: MessagePlacement): Promise<Message[]> {
  const { data } = await sdkListMessages({
    ...(placement === undefined ? {} : { query: { placement } }),
    throwOnError: true,
  });
  return data.items ?? [];
}

/** Published notices a signed-in employee may see, already sorted by the API. */
export function useMessages(placement?: MessagePlacement) {
  return useQuery({
    queryKey: messageKeys.list(placement ?? "ALL"),
    queryFn: () => listMessages(placement),
    staleTime: MESSAGE_STALE_MS,
  });
}

async function adminListMessages(): Promise<Message[]> {
  const { data } = await sdkAdminListMessages({ throwOnError: true });
  return data.items ?? [];
}

/** Every notice, including drafts and hidden ones. */
export function useAdminMessages() {
  return useQuery({
    queryKey: messageKeys.admin(),
    queryFn: adminListMessages,
    staleTime: MESSAGE_STALE_MS,
  });
}

async function messageHistory(messageId: string): Promise<MessageEvent[]> {
  const { data } = await sdkAdminMessageHistory({
    path: { message_id: messageId },
    throwOnError: true,
  });
  return data.items ?? [];
}

/**
 * The placement audit trail for one notice.
 *
 * `enabled` is caller-controlled via a null id so the dialog that shows this
 * does not fetch a history for every row on the page behind it.
 */
export function useMessageHistory(messageId: string | null) {
  return useQuery({
    queryKey: messageKeys.history(messageId ?? ""),
    queryFn: () => messageHistory(messageId as string),
    enabled: messageId !== null && messageId.length > 0,
    staleTime: MESSAGE_STALE_MS,
  });
}

export function useCreateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: MessageCreateRequest): Promise<Message> => {
      const { data } = await sdkAdminCreateMessage({ body, throwOnError: true });
      return data;
    },
    onSuccess: (message) => {
      logger.debug("[messages] created", message.message_id);
      void queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}

export interface UpdateMessageInput {
  messageId: string;
  patch: MessageUpdateRequest;
}

export function useUpdateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, patch }: UpdateMessageInput): Promise<Message> => {
      const { data } = await sdkAdminUpdateMessage({
        path: { message_id: messageId },
        body: patch,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (message) => {
      logger.debug("[messages] updated", message.message_id, message.placement);
      void queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}

export function useDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageId: string): Promise<void> => {
      await sdkAdminDeleteMessage({ path: { message_id: messageId }, throwOnError: true });
    },
    onSuccess: (_result, messageId) => {
      logger.debug("[messages] deleted", messageId);
      void queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}

/** One notice in full, for its own page. */
export function useMessage(messageId: string | undefined) {
  return useQuery({
    queryKey: [...messageKeys.all, "detail", messageId ?? ""],
    queryFn: async () => {
      const { data } = await sdkGetMessage({
        path: { message_id: messageId as string },
        throwOnError: true,
      });
      return data;
    },
    enabled: messageId !== undefined && messageId.length > 0,
    staleTime: 5 * 60_000,
  });
}
